import Guard from 'arg-guard'

/**
 * Defines an operation performed through a service.
 *
 * @export
 * @class ServiceOperation
 */
export default class ServiceOperation {

    /**
     * Creates an instance of ServiceOperation.
     * @param {string} name
     * @param {boolean} started
     * @memberof ServiceOperation
     */
    constructor(name, started = false) {
        Guard.throwIf(name, 'name')

        /**
         * @type {string}
         */
        this.name = name

        /**
         * @type {boolean}
         */
        this.running = started

        /**
         * @type {boolean}
         */
        this.completed = false

        /**
         * @type {boolean}
         */
        this.succeeded = false

        /**
         * @type {?number}
         */
        this.statusCode = null

        /**
         * @type {?Object}
         */
        this.data = undefined
    }

    /**
     * Completes a service operation.
     * @param {boolean} succeeded
     * @param {?Object} data Any data or error info returned
     * @param {number} statusCode Defaults to 200 for a successful operation.
     * @returns {ServiceOperation} itself.
     * @memberof ServiceOperation
     */
    complete(succeeded, data = null, statusCode = 200) {
        Guard.throwIf(succeeded, 'succeeded')

        this.running = false
        this.completed = true
        this.succeeded = succeeded
        this.statusCode = statusCode

        if (data !== undefined && data !== null || this.data !== undefined && this.data !== null)
            this.data = data

        return this
    }

    /**
     * Fails a service operation.
     * @param {Error|?string} error An error or error message
     * @param {number} statusCode Defaults to 500 for a failed operation.
     * @returns {ServiceOperation} itself.
     * @memberof ServiceOperation
     */
    fail(error, statusCode = 500) {
        Guard.throwIf(error, 'error')

        this.complete(false, null, statusCode)

        this.errorObject = error
            ? error.message
                ? error
                : new Error(error)
            : new Error('Service Operation error')

        this.error = this.errorObject ? this.errorObject.message : null
        return this
    }

    /**
     * Throws the wrapped error object back for interrogation.
     * @memberof ServiceOperation
     */
    throw() {
        if (this.errorObject)
            throw this.errorObject
        else if (this.error)
            throw new Error(this.error)
        else
            throw new Error('Service Operation error')
    }

    /**
     * Merges another operation into this one, updating it's info.
     * @param {ServiceOperation} operation
     * @param {boolean} includeData If not set then the data item will be eradicated if it is present.
     * @memberof ServiceOperation
     */
    updateMergeFrom(operation, includeData = false) {
        Guard.throwIf(operation, 'operation')

        this.running = operation.running
        this.completed = operation.completed
        this.succeeded = operation.succeeded
        this.statusCode = operation.statusCode

        if (!includeData && this.data)
            delete this.data
        else if (includeData && operation.data)
            this.data = operation.data

        if (operation.stillPending !== undefined)
            this.stillPending = operation.stillPending

        if (!this.errorObject && operation.errorObject)
            this.errorObject = operation.errorObject

        if (!this.error && operation.error)
            this.error = operation.error
    }

    /**
     * Generates an immediately completed operation.
     * @static
     * @type {ServiceOperation}
     * @memberof ServiceOperation
     */
    static get immediate() {
        return new ServiceOperation('Immediate').complete(true)
    }
}
